import React, { useState } from "react";
import { OPERATING_PRINCIPLES, OFFICES, LEADERSHIP } from "../data/content";
import { Reveal, Tag, RedDot, SectionNum } from "../components/ui-primitives";
import { Linkedin, Mail } from "lucide-react";
import ClosingCTA from "../components/ClosingCTA";
import VerticalTabs from "../components/VerticalTabs";

const STORY_TABS = [
  {
    id: "yesterday",
    label: "Yesterday",
    paragraphs: [
      "DesignIndya was founded in 2008 in Ludhiana with a single, clear mandate across marketing, advertising, and design:",
      {
        emphasis:
          "We connect Indian brands to their audience at rates no single brand can negotiate alone.",
      },
      "Seventeen years later, that mandate hasn't changed. The scale of what we deliver has.",
    ],
  },
  {
    id: "today",
    label: "Today",
    paragraphs: [
      "Today we are a public limited company operating from seven offices across India: Ludhiana, Delhi, Mumbai, Chandigarh, Jaipur, Lucknow, and Shimla.",
      "Our team of 52 works with 150+ brands across Healthcare, FMCG, Education, Automobiles, Jewellery, Real Estate, and Government.",
      {
        emphasis: "We plan, make, place, and measure ₹200+ crore in advertising annually",
        tail: " — through 1,000+ national and regional media partners.",
      },
    ],
  },
  {
    id: "always",
    label: "Always",
    paragraphs: [
      "The recognition we value most is serving a client who has been with us for ten years and doesn't feel the need to look elsewhere.",
      {
        emphasis: "We measure success in years, not campaigns.",
      },
    ],
  },
];

function CompanyStorySection() {
  return (
    <section
      data-testid="company-story"
      className="di-section"
      style={{ background: "var(--color-surface-container-low)" }}
    >
      <div className="di-container">
        <div className="grid grid-cols-1 md:grid-cols-12 md:gap-10">
          {/* Section heading lives above the tab grid, full width on mobile */}
          <div className="md:col-span-12 md:mb-2">
            <Tag>The Company</Tag>
            <h2 className="display-md mt-6 mb-6" style={{ maxWidth: 720 }}>
              Seventeen years. One mandate.
            </h2>
          </div>

          {/* Tabs + panel */}
          <div className="md:col-span-12">
            <VerticalTabs
              testIdPrefix="story"
              defaultId="today"
              alignContent="center"
              tabs={STORY_TABS.map((t) => ({
                id: t.id,
                label: t.label,
                render: () => (
                  <div className="space-y-6">
                    {t.paragraphs.map((p, i) => {
                      // String → plain muted paragraph.
                      if (typeof p === "string") {
                        return (
                          <p
                            key={`${t.id}-p-${i}`}
                            className="body-lg"
                            style={{ color: "var(--color-on-surface-variant)" }}
                          >
                            {p}
                          </p>
                        );
                      }
                      // { lead, emphasis } → plain prefix + bold tail-sentence.
                      if (p.lead) {
                        return (
                          <p
                            key={`${t.id}-p-${i}`}
                            className="body-lg"
                            style={{ color: "var(--color-on-surface-variant)" }}
                          >
                            {p.lead}
                            <strong
                              data-testid="story-emphasis"
                              style={{
                                color: "var(--color-on-surface)",
                                fontWeight: 700,
                              }}
                            >
                              {p.emphasis}
                            </strong>
                          </p>
                        );
                      }
                      // { emphasis, tail } → bold prefix + plain tail-sentence.
                      if (p.tail) {
                        return (
                          <p
                            key={`${t.id}-p-${i}`}
                            className="body-lg"
                            style={{ color: "var(--color-on-surface-variant)" }}
                          >
                            <strong
                              data-testid="story-emphasis"
                              style={{
                                color: "var(--color-on-surface)",
                                fontWeight: 700,
                              }}
                            >
                              {p.emphasis}
                            </strong>
                            {p.tail}
                          </p>
                        );
                      }
                      // { emphasis } only → standalone bold paragraph.
                      return (
                        <p
                          key={`${t.id}-p-${i}`}
                          className="body-lg"
                          data-testid="story-emphasis"
                          style={{
                            color: "var(--color-on-surface)",
                            fontWeight: 700,
                          }}
                        >
                          {p.emphasis}
                        </p>
                      );
                    })}
                  </div>
                ),
              }))}
            />
          </div>
        </div>
      </div>
    </section>
  );
}


// THE GROUP — three entities + one strategic partners card. All four cards
// use the same primary-red label treatment for visual symmetry across the row.
const GROUP_ENTITIES = [
  {
    label: "Parent Company",
    title: "Designindya Advertising",
    body: [
      "Media planning and buying across print, broadcast, OOH, digital, and government advertising. The core business, operating since 2008.",
    ],
  },
  {
    label: "Hyperlocal Arm",
    title: "Hype 'N' Hike Advertising",
    body: [
      "Hyperlocal and retail advertising for Delhi NCR-based clients. Acquired 2023.",
    ],
  },
  {
    label: "Creative Arm",
    title: "Transmitter Agency",
    body: [
      "In-house creative and branding arm — brand identity, design, copywriting, and digital production.",
    ],
    link: { href: "https://transmitteragency.com/", label: "transmitteragency.com" },
  },
  {
    label: "Strategic Partnerships",
    title: "Beyond The Group",
    body: [
      "Specialist partnerships extend our delivery capability into competencies and formats we don't need to own to execute well.",
      "Some of our trusted partners include National Advertising Agency, Decode Advertising, and Qube Cinema.",
    ],
  },
];

// Single-person panel rendered inside the Leadership VerticalTabs panel.
// Replaces the previous LeaderCard hover-flip article. Shows avatar +
// role + bio, with an optional pull-quote (red left border) for the three
// people whose quotes were captured: Rakesh, Suresh, Manish, and now Raman.
function LeaderPanel({ person }) {
  const bioParas = Array.isArray(person.bio) ? person.bio : [person.bio];
  return (
    <article data-testid={`leader-panel-${person.id}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        {bioParas.map((para, i) => (
          <p
            key={`${person.id}-bio-${i}`}
            className="body-lg"
            style={{ color: "var(--color-on-surface-variant)", margin: 0 }}
          >
            {para}
          </p>
        ))}
      </div>

      {/* Identity row — avatar placeholder + LinkedIn + email, below the bio. */}
      <div
        className="flex items-center gap-4 mt-8 pt-6"
        style={{ borderTop: "1px solid var(--color-outline)" }}
      >
        {person.photo ? (
          <img
            src={person.photo}
            alt={`${person.name} — portrait`}
            data-testid={`leader-photo-${person.id}`}
            loading="lazy"
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              objectFit: "cover",
              objectPosition: "center 25%",
              flexShrink: 0,
              filter: "grayscale(100%)",
              background: "var(--color-surface-container)",
            }}
          />
        ) : (
          <div
            aria-hidden
            title={`${person.name} — photo placeholder`}
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "var(--color-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: "1.0625rem",
              fontWeight: 700,
              color: "var(--color-on-primary)",
              letterSpacing: "0.02em",
              flexShrink: 0,
            }}
          >
            {person.initials}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {person.linkedin && (
            <a
              href={person.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid={`leader-linkedin-${person.id}`}
              aria-label={`${person.name} on LinkedIn`}
              className="leader-id-icon"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--color-outline)",
                color: "var(--color-on-surface-variant)",
                transition: "border-color 0.18s ease, color 0.18s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-tertiary)";
                e.currentTarget.style.color = "var(--color-tertiary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-outline)";
                e.currentTarget.style.color = "var(--color-on-surface-variant)";
              }}
            >
              <Linkedin size={16} strokeWidth={1.8} />
            </a>
          )}
          {person.email && (
            <a
              href={`mailto:${person.email}`}
              data-testid={`leader-email-${person.id}`}
              aria-label={`Email ${person.name}`}
              className="leader-id-icon"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--color-outline)",
                color: "var(--color-on-surface-variant)",
                transition: "border-color 0.18s ease, color 0.18s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-tertiary)";
                e.currentTarget.style.color = "var(--color-tertiary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-outline)";
                e.currentTarget.style.color = "var(--color-on-surface-variant)";
              }}
            >
              <Mail size={16} strokeWidth={1.8} />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function AboutPage() {
  return (
    <main data-testid="about-page" style={{ background: "var(--color-surface)" }}>
      {/* HERO */}
      <section style={{ paddingTop: 152, paddingBottom: 64 }}>
        <div className="di-container">
          <Tag>Our Story</Tag>
          <h1 className="display-lg mt-6">
            Built in Ludhiana.
            <br />
            Present across India.
          </h1>
          <div
            className="mt-8 flex flex-wrap gap-x-8 gap-y-3 label-sm"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            <span className="flex items-center gap-2">
              <RedDot size="sm" /> Est. 2008
            </span>
            <span className="flex items-center gap-2">
              <RedDot size="sm" /> Public Limited · June 2024
            </span>
            <span className="flex items-center gap-2">
              <RedDot size="sm" /> Team of 52
            </span>
            <span className="flex items-center gap-2">
              <RedDot size="sm" /> Seven Offices
            </span>
          </div>
        </div>
      </section>

      {/* COMPANY STORY — Yesterday / Today / Always vertical tabs */}
      <CompanyStorySection />

      {/* THE GROUP — three entities + strategic partners */}
      <section
        data-testid="the-group"
        className="di-section"
        style={{ background: "var(--color-surface)" }}
      >
        <div className="di-container">
          <Tag>The Group</Tag>
          <h2 className="display-md mt-6 mb-8">
            More under one roof than you'd expect.
          </h2>
          <div
            data-testid="group-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch"
            style={{ background: "var(--color-surface-container)", gap: "1px" }}
          >
            {GROUP_ENTITIES.map((g, i) => {
              const bodyParas = Array.isArray(g.body) ? g.body : [g.body];
              return (
                <div
                  key={g.title}
                  data-testid={`group-card-${i + 1}`}
                  style={{
                    background: "var(--color-surface-container-lowest)",
                    padding: "var(--space-32)",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <div
                    className="label-md inline-flex items-center gap-2 mb-5"
                    style={{ color: "var(--color-tertiary)" }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--color-tertiary)",
                        display: "inline-block",
                      }}
                    />
                    {g.label}
                  </div>
                  <h3
                    className="headline-md"
                    style={{ marginBottom: 12, color: "var(--color-primary)" }}
                  >
                    {g.title}
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {bodyParas.map((para, pIdx) => (
                      <p
                        key={`${g.title}-body-${pIdx}`}
                        className="body-md"
                        style={{ color: "var(--color-on-surface-variant)", margin: 0 }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                  {g.link && (
                    <a
                      href={g.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`group-card-link-${i + 1}`}
                      className="label-md inline-flex items-center gap-2 mt-5"
                      style={{
                        color: "var(--color-tertiary)",
                        borderBottom: "1px solid var(--color-tertiary)",
                        paddingBottom: 2,
                        alignSelf: "flex-start",
                      }}
                    >
                      {g.link.label} ↗
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section
        data-testid="leadership-section"
        className="di-section"
        style={{ background: "var(--color-surface-container)" }}
      >
        <div className="di-container">
          <div className="mb-8">
            <Tag>The Leadership</Tag>
            <h2 className="display-md mt-6">
              The people behind the work.
            </h2>
          </div>

          <div
            data-testid="leadership-tabs-container"
          >
            <VerticalTabs
              testIdPrefix="leader"
              defaultId={LEADERSHIP[0]?.id}
              alignContent="center"
              tabs={LEADERSHIP.map((p) => ({
                id: p.id,
                label: (
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 2,
                      lineHeight: 1.15,
                    }}
                  >
                    <span>{p.name}</span>
                    <span
                      style={{
                        color: "var(--color-tertiary)",
                        fontFamily: "var(--font-body)",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      {p.role}
                    </span>
                  </span>
                ),
                render: () => <LeaderPanel person={p} />,
              }))}
            />
          </div>
        </div>
      </section>

      {/* WHAT HASN'T CHANGED — sits between Leadership and Where We Are.
          Bridge sentence above tag echoes the Company Story narrative thread. */}
      <section
        data-testid="operating-principles"
        className="di-section"
        style={{ background: "var(--color-surface)" }}
      >
        <div className="di-container">
          <Tag>Our Principles</Tag>
          <h2 className="display-md mt-6 mb-8">
            Seventeen years. Three constants.
          </h2>
          <div
            className="grid grid-cols-1 md:grid-cols-3 items-stretch"
            style={{ background: "var(--color-surface-container)", gap: "1px" }}
          >
            {OPERATING_PRINCIPLES.map((p, i) => {
              const bodyParas = Array.isArray(p.body) ? p.body : [p.body];
              return (
                <div
                  key={p.title}
                  data-testid={`principle-card-${i + 1}`}
                  style={{
                    background: "var(--color-surface-container-lowest)",
                    padding: "var(--space-32)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <RedDot />
                    <SectionNum>{String(i + 1).padStart(2, "0")}</SectionNum>
                  </div>
                  <h3 className="headline-md mb-4">{p.title}</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {bodyParas.map((para, pIdx) => (
                      <p
                        key={`${p.title}-body-${pIdx}`}
                        className="body-md"
                        style={{ color: "var(--color-on-surface-variant)", margin: 0 }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHERE WE ARE — 4+3 office grid with three-line addresses; sits
          immediately before the Closing CTA so the page ends on a
          "here's where we are → get in touch" rhythm. */}
      <section
        data-testid="offices-section"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <Tag>Where We Are</Tag>
          <h2 className="display-md mt-6 mb-8">
            Seven offices. One network.
          </h2>
          <div
            data-testid="office-grid"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            style={{ background: "var(--color-surface-container)", gap: "1px" }}
          >
            {OFFICES.map((o, i) => (
              <div
                key={o.city}
                data-testid={`office-card-${i + 1}`}
                style={{
                  background: "var(--color-surface-container-lowest)",
                  padding: "var(--space-32)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 220,
                }}
              >
                <SectionNum>{String(i + 1).padStart(2, "0")}</SectionNum>
                <div style={{ marginTop: 24 }}>
                  <div
                    className="display-sm"
                    style={{
                      color: "var(--color-primary)",
                      marginBottom: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{o.city}</span>
                    {o.note && (
                      <span
                        data-testid={`office-note-${i + 1}`}
                        style={{
                          color: "var(--color-tertiary)",
                          fontFamily: "var(--font-body)",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          lineHeight: 1,
                          display: "inline-block",
                        }}
                      >
                        {o.note}
                      </span>
                    )}
                  </div>
                  {o.address && (
                    <p
                      className="body-sm"
                      style={{
                        color: "var(--color-on-surface-variant)",
                        margin: 0,
                        lineHeight: 1.55,
                        minHeight: "calc(0.875rem * 1.55 * 3)",
                      }}
                    >
                      {o.address}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </main>
  );
}
